import React, { useState, useEffect, useCallback, useContext, useRef } from 'react';
import LeftMenu from '../../components/LeftMenu';
import withAuth from '../../common/auth';
import { ToastContainer, toast } from 'react-toastify';
import { Slide } from 'react-toastify';
import { FiPackage, FiRefreshCcw, FiDownloadCloud, FiPrinter, FiX, FiFileText, FiFile } from "react-icons/fi";
import { TabContext } from '../../components/TabbedLayout';
import { ReportEmbedContext } from '../reportEmbedContext';
import { GET_SALES_RETURN_REPORT, BASE_URL } from '../../utlilities/config';
import axios from 'axios';
import ReturnPrint from '../../components/ReturnPrint';
import * as XLSX from 'xlsx';
import jsPDF from 'jspdf';
import autoTable from 'jspdf-autotable';
import HighlightMetricsModal from './components/HighlightMetricsModal';


// Enhanced Spinner Component
const Spinner = () => (
    <div className="fixed inset-0 z-[60] flex items-center justify-center bg-white bg-opacity-80 backdrop-blur-sm">
      <div className="flex flex-col items-center gap-3">
        <div className="animate-spin rounded-full h-16 w-16 border-t-4 border-b-4 border-indigo-600"></div>
        <p className="text-indigo-600 font-medium">Loading...</p>
      </div>
    </div>
);

// Helper function to format date in local timezone (IST)
const formatLocalDate = (date) => {
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
};

/** From sales-return report row: IP vs OP + Credit/Debit (IsIPCredit) for IP bills. */
const resolveBillIpCreditMeta = (row) => {
  const rawIp = row?.IPNo ?? row?.IpNo ?? row?.ipNo;
  const ipNum =
    rawIp !== null && rawIp !== undefined && String(rawIp).trim() !== ''
      ? Number(String(rawIp).trim())
      : NaN;
  const hasIp = !Number.isNaN(ipNum) && ipNum > 0;
  const ipDisplay = hasIp ? String(ipNum) : null;

  const v = row?.IsIPCredit ?? row?.isIPCredit;
  let isCredit = false;
  if (v === true || v === 1 || v === '1' || String(v).toLowerCase() === 'true') isCredit = true;
  else if (v === false || v === 0 || v === '0' || String(v).toLowerCase() === 'false') isCredit = false;

  return {
    hasIp,
    ipDisplay,
    isCredit,
    creditDebitLabel: isCredit ? 'Credit' : 'Debit',
  };
};

const billTypeExportString = (row) => {
  const m = resolveBillIpCreditMeta(row);
  if (m.hasIp) return `IP ${m.ipDisplay} · ${m.creditDebitLabel}`;
  return 'OP return';
};

// Build initial filters
const buildInitialFilters = (filters = []) => {
  const today = new Date();
  const todayFormatted = formatLocalDate(today);

  return filters.reduce((accumulator, filter) => {
    if (filter.type === 'date') {
      // Set both fromDate and toDate to today by default
      const dateValue = todayFormatted;
      return { ...accumulator, [filter.id]: dateValue };
    }

    if (filter.type === 'select') {
      const defaultValue = filter.options?.[0]?.value ?? '';
      return { ...accumulator, [filter.id]: defaultValue };
    }

    return { ...accumulator, [filter.id]: '' };
  }, {});
};

// Report configuration
const reportConfig = {
  filters: [
    { id: 'fromDate', type: 'date', label: 'From Date' },
    { id: 'toDate', type: 'date', label: 'To Date' },
  ],

  table: {
    columns: [
      { key: 'InvoiceNo', label: 'Invoice No' },
      { key: 'BillType', label: 'Bill type' },
      { key: 'EntryDate', label: 'Date' },
      { key: 'PatientName', label: 'Patient Name' },
      { key: 'DoctorName', label: 'Doctor Name' },
      { key: 'DiscountAmount', label: 'Discount' },
      { key: 'TotalAmount', label: 'Return Amount' },
      { key: 'Actions', label: 'Actions' },
    ],
  },
};

// Export Format Selection Modal
const ExportFormatModal = ({ isOpen, onClose, onSelectFormat }) => {
    if (!isOpen) return null;

    return (
        <div className="fixed inset-0 z-[1100] flex items-center justify-center bg-black bg-opacity-60 backdrop-blur-sm">
            <div className="bg-white rounded-2xl shadow-2xl w-96 max-w-md transform transition-transform duration-300 ease-out">
                <div className="bg-gradient-to-r from-indigo-600 to-purple-600 px-6 py-4 flex justify-between items-center rounded-t-2xl">
                    <h2 className="text-xl font-semibold text-white flex items-center">
                        <FiDownloadCloud className="mr-2" /> Export Report
                    </h2>
                    <button
                        onClick={onClose}
                        className="text-white hover:bg-white hover:bg-opacity-20 rounded-full p-1 transition-colors duration-200"
                        aria-label="Close modal"
                    >
                        <FiX size={20} />
                    </button>
                </div>
                <div className="p-6">
                    <p className="text-sm text-slate-600 mb-4">Choose export format:</p>
                    <div className="flex flex-col gap-3">
                        <button
                            onClick={() => onSelectFormat('excel')}
                            className="flex items-center gap-3 p-4 rounded-lg border-2 border-slate-200 hover:border-indigo-500 hover:bg-indigo-50 transition-all duration-200 group"
                        >
                            <div className="w-10 h-10 rounded-lg bg-green-100 flex items-center justify-center group-hover:bg-green-200 transition-colors">
                                <FiFileText className="text-green-600" size={20} />
                            </div>
                            <div className="flex-1 text-left">
                                <p className="font-semibold text-slate-800">Export to Excel</p>
                                <p className="text-xs text-slate-500">Download as .xlsx file</p>
                            </div>
                        </button>
                        <button
                            onClick={() => onSelectFormat('pdf')}
                            className="flex items-center gap-3 p-4 rounded-lg border-2 border-slate-200 hover:border-indigo-500 hover:bg-indigo-50 transition-all duration-200 group"
                        >
                            <div className="w-10 h-10 rounded-lg bg-red-100 flex items-center justify-center group-hover:bg-red-200 transition-colors">
                                <FiFile className="text-red-600" size={20} />
                            </div>
                            <div className="flex-1 text-left">
                                <p className="font-semibold text-slate-800">Export to PDF</p>
                                <p className="text-xs text-slate-500">Download as .pdf file</p>
                            </div>
                        </button>
                    </div>
                </div>
            </div>
        </div>
    );
};

export const SalesReturnBillWise = () => {
    const tabContext = useContext(TabContext);
    const embedded = useContext(ReportEmbedContext);
    const [loading, setLoading] = useState(false);
    const [filters, setFilters] = useState(() => buildInitialFilters(reportConfig.filters));
    const [isMetricsModalOpen, setIsMetricsModalOpen] = useState(false);
    const [salesData, setSalesData] = useState([]);
    const token = localStorage.getItem('accessToken');
    
    // Print component state
    const [printData, setPrintData] = useState(null);
    const [currentPage, setCurrentPage] = useState(1);
    const [totalPages, setTotalPages] = useState(1);
    const returnPrintRef = useRef(null);
    const [sysInfo, setSysInfo] = useState({});
    const [userInfo, setUserInfo] = useState({});
    const [generalsettings, setGeneralSettings] = useState({});
    const [showExportModal, setShowExportModal] = useState(false);


    const handleFilterChange = (filterId, value) => {
        setFilters((prev) => ({
            ...prev,
            [filterId]: value,
        }));
    };

    const handleRunReport = () => {
        getSalesReport();
    }; 

    const handleExport = () => {
        if (salesData.length === 0) {
            toast.error('No data to export', { autoClose: 2000 });
            return;
        }
        setShowExportModal(true);
    };

    const getCalculatedDiscountFromItems = (items = []) => {
        return items.reduce((sum, item) => {
            const qty = parseFloat(item.qty || 0);
            const salesPrice = parseFloat(item.salesPrice || 0);
            const netTotal = parseFloat(item.netTotal || 0);
            const taxAmount = parseFloat(item.taxAmount || 0);
            const amountBeforeDiscount = salesPrice * qty;
            const finalAmount = netTotal + taxAmount;
            return sum + Math.max(0, amountBeforeDiscount - finalAmount);
        }, 0);
    };

    const getRowDiscount = (row) => {
        const calculatedDiscount = getCalculatedDiscountFromItems(row?.Items || []);
        if (calculatedDiscount > 0) return calculatedDiscount;
        return parseFloat(row?.DiscountAmount || 0);
    };

    const exportToExcel = () => {
        try {
            // Prepare data for Excel - include items for each bill
            const excelData = [];
            
            salesData.forEach((row) => {
                const items = row.Items || [];
                
                // Add main bill row
                excelData.push({
                    'Invoice No': row.InvoiceNo,
                    'Bill type': billTypeExportString(row),
                    'Date': new Date(row.EntryDate).toLocaleDateString('en-GB'),
                    'Patient Name': row.PatientName || '',
                    'Doctor Name': row.DoctorName || '-',
                    'Discount': Number(getRowDiscount(row).toFixed(2)),
                    'Return Amount': Number(parseFloat(row.TotalAmount || 0).toFixed(2)),
                    'Item Name': '',
                    'Qty': '',
                    'MRP': '',
                    'Sales Price': '',
                    'Tax Amount': '',
                    'Net Total': ''
                });
                
                // Add item rows
                items.forEach((item) => {
                    excelData.push({
                        'Invoice No': '',
                        'Bill type': '',
                        'Date': '',
                        'Patient Name': '',
                        'Doctor Name': '',
                        'Discount': '',
                        'Return Amount': '',
                        'Item Name': item.itemName || '-',
                        'Qty': Number(parseFloat(item.qty || 0).toFixed(2)),
                        'MRP': Number(parseFloat(item.mrp || 0).toFixed(2)),
                        'Sales Price': Number(parseFloat(item.salesPrice || 0).toFixed(2)),
                        'Tax Amount': Number(parseFloat(item.taxAmount || 0).toFixed(2)),
                        'Net Total': Number(parseFloat(item.netTotal || 0).toFixed(2))
                    });
                });
            });

            // Create workbook and worksheet
            const wb = XLSX.utils.book_new();
            const ws = XLSX.utils.json_to_sheet(excelData);

            // Set column widths
            const colWidths = [
                { wch: 12 }, // Invoice No
                { wch: 22 }, // Bill type
                { wch: 12 }, // Date
                { wch: 25 }, // Patient Name
                { wch: 20 }, // Doctor Name
                { wch: 12 }, // Discount
                { wch: 15 }, // Return Amount
                { wch: 30 }, // Item Name
                { wch: 10 }, // Qty
                { wch: 12 }, // MRP
                { wch: 12 }, // Sales Price
                { wch: 12 }, // Tax Amount
                { wch: 12 }, // Net Total
            ];
            ws['!cols'] = colWidths;

            // Add worksheet to workbook
            XLSX.utils.book_append_sheet(wb, ws, 'Sales Return Bill Wise');

            // Generate filename with date range
            const fromDate = filters.fromDate ? new Date(filters.fromDate).toLocaleDateString('en-GB').replace(/\//g, '-') : 'All';
            const toDate = filters.toDate ? new Date(filters.toDate).toLocaleDateString('en-GB').replace(/\//g, '-') : 'All';
            const filename = `Sales_Return_Bill_Wise_${fromDate}_to_${toDate}.xlsx`;

            // Write file
            XLSX.writeFile(wb, filename);
            toast.success('Excel file exported successfully', { autoClose: 2000 });
            setShowExportModal(false);
        } catch (error) {
            console.error('Error exporting to Excel:', error);
            toast.error('Failed to export to Excel', { autoClose: 2000 });
        }
    };

    const exportToPDF = () => {
        try {
            const doc = new jsPDF('landscape', 'mm', 'a4');
            
            // Add title
            doc.setFontSize(16);
            doc.text('Sales Return Bill Wise Report', 14, 15);
            
            // Add date range
            doc.setFontSize(10);
            const fromDate = filters.fromDate ? new Date(filters.fromDate).toLocaleDateString('en-GB') : 'All';
            const toDate = filters.toDate ? new Date(filters.toDate).toLocaleDateString('en-GB') : 'All';
            doc.text(`Date Range: ${fromDate} to ${toDate}`, 14, 22);
            
            // Prepare table data
            const tableData = [];
            
            salesData.forEach((row) => {
                // Add main bill row
                tableData.push([
                    row.InvoiceNo,
                    billTypeExportString(row),
                    new Date(row.EntryDate).toLocaleDateString('en-GB'),
                    row.PatientName || '',
                    row.DoctorName || '-',
                    `₹${getRowDiscount(row).toFixed(2)}`,
                    `₹${parseFloat(row.TotalAmount || 0).toFixed(2)}`,
                    '', // Item Name
                    '', // Qty
                    '', // MRP
                    '', // Sales Price
                    '', // Tax Amount
                    ''  // Net Total
                ]);
                
                // Add item rows
                const items = row.Items || [];
                items.forEach((item) => {
                    tableData.push([
                        '', // Invoice No
                        '', // Bill type
                        '', // Date
                        '', // Patient Name
                        '', // Doctor Name
                        '', // Discount
                        '', // Return Amount
                        item.itemName || '-',
                        parseFloat(item.qty || 0).toFixed(2),
                        `₹${parseFloat(item.mrp || 0).toFixed(2)}`,
                        `₹${parseFloat(item.salesPrice || 0).toFixed(2)}`,
                        `₹${parseFloat(item.taxAmount || 0).toFixed(2)}`,
                        `₹${parseFloat(item.netTotal || 0).toFixed(2)}`
                    ]);
                });
            });
            
            // Add table
            autoTable(doc, {
                head: [['Invoice No', 'Bill type', 'Date', 'Patient Name', 'Doctor Name', 'Discount', 'Return Amount', 'Item Name', 'Qty', 'MRP', 'Sales Price', 'Tax Amount', 'Net Total']],
                body: tableData,
                startY: 28,
                styles: { fontSize: 7, cellPadding: 2 },
                headStyles: { fillColor: [99, 102, 241], textColor: 255, fontStyle: 'bold' },
                alternateRowStyles: { fillColor: [249, 250, 251] },
                columnStyles: {
                    0: { cellWidth: 18 },
                    1: { cellWidth: 26 },
                    2: { cellWidth: 18 },
                    3: { cellWidth: 26 },
                    4: { cellWidth: 22 },
                    5: { cellWidth: 18 },
                    6: { cellWidth: 22 },
                    7: { cellWidth: 32 },
                    8: { cellWidth: 12 },
                    9: { cellWidth: 18 },
                    10: { cellWidth: 18 },
                    11: { cellWidth: 18 },
                    12: { cellWidth: 18 }
                },
                margin: { top: 28, right: 14, bottom: 14, left: 14 }
            });
            
            // Generate filename with date range
            const fromDateStr = filters.fromDate ? new Date(filters.fromDate).toLocaleDateString('en-GB').replace(/\//g, '-') : 'All';
            const toDateStr = filters.toDate ? new Date(filters.toDate).toLocaleDateString('en-GB').replace(/\//g, '-') : 'All';
            const filename = `Sales_Return_Bill_Wise_${fromDateStr}_to_${toDateStr}.pdf`;
            
            // Save PDF
            doc.save(filename);
            toast.success('PDF file exported successfully', { autoClose: 2000 });
            setShowExportModal(false);
        } catch (error) {
            console.error('Error exporting to PDF:', error);
            toast.error('Failed to export to PDF', { autoClose: 2000 });
        }
    };

    const handleExportFormatSelect = (format) => {
        if (format === 'excel') {
            exportToExcel();
        } else if (format === 'pdf') {
            exportToPDF();
        }
    };

    // Load system info and settings
    useEffect(() => {
        const loadSystemInfo = async () => {
            const isElectron = () => {
                return window && window.process && window.process.type;
            };
            
            if (isElectron()) {
                const ipcRenderer = window.require('electron').ipcRenderer;
                const sysinfo = await ipcRenderer.invoke('store-get', 'sysInfo');
                const userinfo = await ipcRenderer.invoke('store-get', 'userInfo');
                const generalSetting = await ipcRenderer.invoke('store-get', 'generalopsettings');
                
                setSysInfo(sysinfo || {});
                setUserInfo(userinfo || {});
                setGeneralSettings(generalSetting || {});
            }
        };
        
        loadSystemInfo();
    }, []);

    // Format date for display
    const formatBilledDate = (date) => {
        if (!date) return '';
        const months = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];
        const dateObj = new Date(date);
        if (isNaN(dateObj.getTime())) return date;
        
        const day = dateObj.getDate().toString().padStart(2, '0');
        const month = months[dateObj.getMonth()];
        const year = dateObj.getFullYear();
        
        let hours = dateObj.getHours();
        const minutes = dateObj.getMinutes().toString().padStart(2, '0');
        const ampm = hours >= 12 ? 'PM' : 'AM';
        hours = hours % 12;
        hours = hours ? hours : 12;
        
        return `${day}-${month}-${year} ${hours}:${minutes} ${ampm}`;
    };

    // Transform report row data to ReturnPrint format
    const transformRowToPrintData = (row) => {
        const items = row.Items || [];
        
        // Transform items to product format
        const products = items.map((item) => {
            // Parse expiry date if available
            let expiryDate = '';
            if (item.expiryDate) {
                expiryDate = item.expiryDate;
            }
            
            return {
                id: item.itemNo || 0,
                name: item.itemName || '',
                batch: {
                    batchNo: item.batchCode || '',
                    expiryDate: expiryDate,
                    mrp: parseFloat(item.mrp || item.salesPrice || 0),
                    rate: parseFloat(item.salesPrice || 0),
                    gstRate: parseFloat(item.gstRate || 0),
                    availableQty: parseFloat(item.qty || 0),
                    purchasePrice: 0
                },
                qty: parseFloat(item.qty || 0),
                rate: parseFloat(item.salesPrice || 0),
                amount: parseFloat(item.netTotal || 0) + parseFloat(item.taxAmount || 0), // Amount before discount
                discountAmount: parseFloat(item.discountAmount || 0),
                gstAmount: parseFloat(item.taxAmount || 0),
                gstRate: parseFloat(item.gstRate || 0),
                totalAmount: parseFloat(item.netTotal || 0), // Net total after discount
                mrp: parseFloat(item.mrp || item.salesPrice || 0),
                discount: item.discountPercentage ? `${item.discountPercentage}p` : ''
            };
        });
        
        // Calculate totals
        const calculateTotalAmount = () => {
            return products.reduce((sum, product) => sum + parseFloat(product.amount || 0), 0).toFixed(2);
        };
        
        const calculateTotalDiscount = () => {
            return products.reduce((sum, product) => sum + parseFloat(product.discountAmount || 0), 0).toFixed(2);
        };
        
        const calculateTotalGST = () => {
            return products.reduce((sum, product) => sum + parseFloat(product.gstAmount || 0), 0).toFixed(2);
        };
        
        const calculateGrandTotal = () => {
            return products.reduce((sum, product) => sum + parseFloat(product.totalAmount || 0), 0).toFixed(2);
        };
        
        const totalAmount = parseFloat(calculateTotalAmount());
        const totalDiscount = parseFloat(calculateTotalDiscount());
        // For sales return printout, use API TotalAmount as final payable/return amount.
        const grandTotal = parseFloat(row.TotalAmount || calculateGrandTotal() || 0);
        const balanceDue = grandTotal;
        
        return {
            invoiceNumber: row.InvoiceNo?.toString() || '',
            selectedPatient: {
                PatientID: row.PatientID || 0,
                PatientName: row.PatientName || '',
                Age: row.Age || '',
                Gender: row.Gender || '',
                MobileNo: row.MobileNo || ''
            },
            selectedDoctor: {
                value: row.DoctorID || 0,
                label: row.DoctorName || ''
            },
            date: formatBilledDate(row.EntryDate),
            products: products,
            balance_due: balanceDue,
            calculateTotal: calculateTotalAmount,
            calculateGrandTotal: calculateGrandTotal,
            calculateTotalDiscount: calculateTotalDiscount,
            calculateTotalGST: calculateTotalGST,
            grand_total: grandTotal,
            payingAmount: 0
        };
    };

    const handleReprint = useCallback(async (row) => {
        try {
            // Use return data directly from the report row
            const items = row.Items || [];
            
            // Transform return items to products format
            const products = items.map((item) => {
                const qty = parseFloat(item.qty || 0);
                const salesPrice = parseFloat(item.salesPrice || 0);
                const mrp = parseFloat(item.mrp || salesPrice);
                const taxAmount = parseFloat(item.taxAmount || 0);
                const netTotal = parseFloat(item.netTotal || 0);
                const gstRate = parseFloat(item.taxCode || 0);
                
                // Calculate amount before discount (salesPrice * qty)
                const amountBeforeDiscount = salesPrice * qty;
                
                // Total amount for this item (netTotal + taxAmount)
                const itemTotal = netTotal + taxAmount;
                
                // Discount should be the final reduction on billed amount (excluding GST double count).
                // Example: before 200, final 180 => discount 20.
                const discountAmount = Math.max(0, amountBeforeDiscount - itemTotal);
                
                return {
                    id: item.itemNo || 0,
                    name: item.itemName || '',
                    batch: {
                        batchNo: item.batchCode || '',
                        expiryDate: '', // Not available in return report
                        mrp: mrp,
                        rate: salesPrice,
                        gstRate: gstRate,
                        availableQty: qty,
                        purchasePrice: 0 // Not available in return report
                    },
                    qty: qty,
                    rate: salesPrice / (1 + (gstRate / 100)), // Base rate (excluding GST)
                    amount: amountBeforeDiscount.toFixed(2), // Amount before discount
                    discountAmount: discountAmount.toFixed(2),
                    gstAmount: taxAmount.toFixed(2),
                    gstRate: gstRate,
                    totalAmount: itemTotal.toFixed(2), // Total amount (netTotal + tax)
                    mrp: mrp,
                    discount: '' // Discount percentage not available in return report
                };
            });
            
            // Calculate totals
            const calculateTotalAmount = () => {
                return products.reduce((sum, product) => sum + parseFloat(product.amount || 0), 0).toFixed(2);
            };
            
            const calculateTotalDiscount = () => {
                return products.reduce((sum, product) => sum + parseFloat(product.discountAmount || 0), 0).toFixed(2);
            };
            
            const calculateTotalGST = () => {
                return products.reduce((sum, product) => sum + parseFloat(product.gstAmount || 0), 0).toFixed(2);
            };
            
            const calculateGrandTotal = () => {
                // Use TotalAmount from API for print grand total.
                return parseFloat(row.TotalAmount || 0).toFixed(2);
            };
            
            const grandTotal = parseFloat(calculateGrandTotal());
            
            // Format date
            const formatBilledDate = (date) => {
                if (!date) return '';
                const months = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];
                const dateObj = new Date(date);
                if (isNaN(dateObj.getTime())) return date;
                
                const day = dateObj.getDate().toString().padStart(2, '0');
                const month = months[dateObj.getMonth()];
                const year = dateObj.getFullYear();
                
                let hours = dateObj.getHours();
                const minutes = dateObj.getMinutes().toString().padStart(2, '0');
                const ampm = hours >= 12 ? 'PM' : 'AM';
                hours = hours % 12;
                hours = hours ? hours : 12;
                
                return `${day}-${month}-${year} ${hours}:${minutes} ${ampm}`;
            };
            
            // Set print data
            setPrintData({
                invoiceNumber: row.InvoiceNo?.toString() || '',
                selectedPatient: {
                    PatientID: row.PatientID || 0,
                    PatientName: row.PatientName || '',
                    Age: '', // Not available in return report
                    Gender: '', // Not available in return report
                    MobileNo: '' // Not available in return report
                },
                selectedDoctor: {
                    value: row.DoctorID || 0,
                    label: row.DoctorName || ''
                },
                date: formatBilledDate(row.EntryDate),
                products: products,
                balance_due: grandTotal,
                calculateTotal: calculateTotalAmount,
                calculateGrandTotal: calculateGrandTotal,
                calculateTotalDiscount: calculateTotalDiscount,
                calculateTotalGST: calculateTotalGST,
                grand_total: grandTotal,
                payingAmount: 0
            });
            
            // Trigger print after a short delay
            setTimeout(async () => {
                if (returnPrintRef.current) {
                    try {
                        await returnPrintRef.current.handleDotMatrixPrint();
                        // Clear print data after successful print
                        setTimeout(() => {
                            setPrintData(null);
                        }, 1000);
                    } catch (error) {
                        console.error('Print error:', error);
                        // Clear print data even on error
                        setPrintData(null);
                    }
                }
            }, 500);
        } catch (error) {
            console.error('Failed to prepare print data:', error);
            toast.error('Failed to prepare print data', { autoClose: 2000 });
        }
    }, []);

    const getSalesReport = useCallback(async () => {
        setLoading(true);
        console.log('Filters:', filters);
        try {
            // Convert date strings to Date objects and format as YYYY-MM-DD
            const fromDate = filters.fromDate ? new Date(filters.fromDate) : null;
            const toDate = filters.toDate ? new Date(filters.toDate) : null;
            
            // Format date as YYYY-MM-DD
            const formatDate = (date) => {
                if (!date) return '';
                const year = date.getFullYear();
                const month = String(date.getMonth() + 1).padStart(2, '0');
                const day = String(date.getDate()).padStart(2, '0');
                return `${year}-${month}-${day}`;
            };
            
            const response = await axios.get(`${BASE_URL}${GET_SALES_RETURN_REPORT}`, {
                headers: {
                    'XApiKey': process.env.REACT_APP_API_KEY,
                    'Authorization': `Bearer ${token}`,
                },
                params: {
                    'rpType': 'bill',
                    'fromDate': formatDate(fromDate),
                    'toDate': formatDate(toDate),
                    'departmentID': localStorage.getItem('department_id') || '',
                    'searchKey': 0,
                    'patientId': 0,
                    'itemNo': 0,
                },
            });
            if (response.status === 200) {
                const data = response.data.data || [];
                // Sort by InvoiceNo in descending order
                const sortedData = data.sort((a, b) => {
                    const invoiceA = a.InvoiceNo || '';
                    const invoiceB = b.InvoiceNo || '';
                    // Convert to number if possible, otherwise compare as strings
                    const numA = parseInt(invoiceA) || 0;
                    const numB = parseInt(invoiceB) || 0;
                    if (numA !== 0 || numB !== 0) {
                        return numB - numA; // Descending order
                    }
                    return invoiceB.localeCompare(invoiceA); // Descending order for strings
                });
                setSalesData(sortedData);
                console.log('Sales report:', sortedData);
            } else {
                toast.error('Failed to fetch sales report', { autoClose: 1000 });
            }
        } catch (error) {
            console.error('Failed to fetch sales report:', error);
            toast.error('Failed to fetch sales report', { autoClose: 1000 });
        }
        finally {
            setLoading(false);
        }
    }, [filters, token]);

    return (
        <div className="flex h-full bg-background">
            {/* <LeftMenu /> */}
            <ToastContainer
                position="top-center"
                autoClose={1000}
                limit={1}
                hideProgressBar={false}
                newestOnTop={false}
                closeOnClick
                rtl={false}
                pauseOnFocusLoss
                draggable
                pauseOnHover
                theme="light"
                transition={Slide}
            />
            
            {loading && <Spinner />}

            <div className="flex-1 flex flex-col h-full overflow-hidden">
                <header className="shrink-0 border-b border-slate-200 bg-white px-3 py-2.5 mb-2">
                    <div className="flex flex-col gap-3">
                        {!embedded && (
                            <div className="min-w-0">
                                <p className="text-xs font-semibold uppercase tracking-widest text-indigo-500">
                                    Sales Reports
                                </p>
                                <h1 className="text-lg font-bold text-slate-800">
                                    Sales Return Bill Wise
                                </h1>
                            </div>
                        )}
                        <div className="flex flex-col gap-3 sm:flex-row sm:items-end sm:justify-between">
                            <form
                                className="flex flex-wrap items-end gap-x-4 gap-y-2 min-w-0 flex-1"
                                onSubmit={(e) => e.preventDefault()}
                            >
                                {reportConfig.filters.map((filter) => {
                                    if (filter.type === 'date') {
                                        return (
                                            <label key={filter.id} className="flex flex-col gap-1.5 text-xs font-medium text-slate-600 shrink-0">
                                                {filter.label}
                                                <input
                                                    type="date"
                                                    value={filters[filter.id] ?? ''}
                                                    onChange={(event) => handleFilterChange(filter.id, event.target.value)}
                                                    className="w-[min(100%,11rem)] rounded-lg border border-slate-200 bg-white px-2.5 py-1.5 text-sm text-slate-700 shadow-sm focus:border-indigo-400 focus:ring-2 focus:ring-indigo-200 transition"
                                                />
                                            </label>
                                        );
                                    }

                                    if (filter.type === 'select') {
                                        return (
                                            <label key={filter.id} className="flex flex-col gap-1.5 text-xs font-medium text-slate-600 shrink-0">
                                                {filter.label}
                                                <select
                                                    value={filters[filter.id] ?? ''}
                                                    onChange={(event) => handleFilterChange(filter.id, event.target.value)}
                                                    className="min-w-[10rem] rounded-lg border border-slate-200 bg-white px-2.5 py-1.5 text-sm text-slate-700 shadow-sm focus:border-indigo-400 focus:ring-2 focus:ring-indigo-200 transition"
                                                >
                                                    {(filter.options ?? []).map((option) => (
                                                        <option key={option.value} value={option.value}>
                                                            {option.label}
                                                        </option>
                                                    ))}
                                                </select>
                                            </label>
                                        );
                                    }

                                    return null;
                                })}
                            </form>
                            <div className="flex flex-wrap items-center justify-end gap-2 sm:gap-3 shrink-0">
                                <button
                                    type="button"
                                    onClick={handleRunReport}
                                    className="inline-flex items-center gap-2 rounded-full bg-indigo-500 px-5 py-2 text-sm font-semibold text-white shadow-sm transition hover:bg-indigo-600"
                                >
                                    <FiRefreshCcw size={16} />
                                    Run report
                                </button>
                                <button
                                    type="button"
                                    onClick={() => setFilters(buildInitialFilters(reportConfig.filters))}
                                    className="inline-flex items-center gap-2 rounded-full border border-slate-200 px-5 py-2 text-sm font-semibold text-slate-600 shadow-sm transition hover:border-indigo-300 hover:text-indigo-500"
                                >
                                    Reset
                                </button>
                                <button
                                    type="button"
                                    onClick={handleExport}
                                    className="inline-flex items-center gap-2 rounded-full border border-slate-200 px-5 py-2 text-sm font-semibold text-slate-600 shadow-sm transition hover:border-indigo-300 hover:text-indigo-500"
                                >
                                    <FiDownloadCloud size={16} />
                                    Export
                                </button>
                            </div>
                        </div>
                    </div>
                </header>

                <main className="flex-1 overflow-hidden flex flex-col">
                    <div className="flex flex-col gap-6 flex-1 min-h-0">
                        <HighlightMetricsModal
                            isOpen={isMetricsModalOpen}
                            onOpen={() => setIsMetricsModalOpen(true)}
                            onClose={() => setIsMetricsModalOpen(false)}
                        >
                            <div className="grid gap-4 md:grid-cols-4">
                                <div className="rounded-xl border border-indigo-100 bg-indigo-50/60 p-3 text-slate-700 shadow-sm">
                                    <p className="text-xs font-semibold uppercase tracking-wide text-indigo-500">
                                        Total Bills
                                    </p>
                                    <p className="mt-1 text-xl font-semibold text-indigo-600">{salesData.length}</p>
                                </div>
                                <div className="rounded-xl border border-indigo-100 bg-indigo-50/60 p-3 text-slate-700 shadow-sm">
                                    <p className="text-xs font-semibold uppercase tracking-wide text-indigo-500">
                                        Total Amount
                                    </p>
                                    <p className="mt-1 text-xl font-semibold text-indigo-600">
                                        ₹{salesData.reduce((sum, item) => sum + (parseFloat(item.TotalAmount || 0)), 0).toFixed(2)}
                                    </p>
                                </div>
                                <div className="rounded-xl border border-indigo-100 bg-indigo-50/60 p-3 text-slate-700 shadow-sm">
                                    <p className="text-xs font-semibold uppercase tracking-wide text-indigo-500">
                                        Total Discount
                                    </p>
                                    <p className="mt-1 text-xl font-semibold text-indigo-600">
                                        ₹{salesData.reduce((sum, item) => sum + getRowDiscount(item), 0).toFixed(2)}
                                    </p>
                                </div>
                                <div className="rounded-xl border border-indigo-100 bg-indigo-50/60 p-3 text-slate-700 shadow-sm">
                                    <p className="text-xs font-semibold uppercase tracking-wide text-indigo-500">
                                        Total Pending
                                    </p>
                                    <p className="mt-1 text-xl font-semibold text-indigo-600">
                                        ₹{salesData.reduce((sum, item) => sum + (parseFloat(item.BalanceDue || 0)), 0).toFixed(2)}
                                    </p>
                                </div>
                            </div>
                        </HighlightMetricsModal>

                        {/* Table Section */}
                        <section className="rounded-2xl border border-slate-200 bg-white p-4 shadow-sm flex flex-col flex-1 min-h-0">
                            <div className="flex flex-col gap-3 md:flex-row md:items-center md:justify-between flex-shrink-0">
                                <div>
                                    <p className="text-sm text-slate-500">
                                        Showing <span className="font-semibold text-slate-700">{salesData.length}</span> datas for this report.
                                    </p>
                                </div>
                            </div>
                            <div className="mt-4 flex-1 overflow-auto min-h-0 rounded-xl border border-slate-200">
                                <table className="min-w-full divide-y divide-slate-200 text-sm text-slate-700">
                                    <thead className="bg-indigo-100 sticky top-0 z-10">
                                        <tr>
                                            {reportConfig.table.columns.map((column) => (
                                                <th
                                                    key={column.key}
                                                    scope="col"
                                                    className="px-4 py-3 text-left text-xs font-semibold uppercase tracking-wide text-slate-500"
                                                >
                                                    {column.label}
                                                </th>
                                            ))}
                                        </tr>
                                    </thead>
                                    <tbody className="divide-y divide-slate-100 bg-white">
                                        {salesData.length > 0 ? (
                                            salesData.map((row, index) => {
                                                const items = row.Items || [];
                                                const billMeta = resolveBillIpCreditMeta(row);

                                                return (
                                                    <React.Fragment key={`${row.InvoiceNo}_${index}`}>
                                                        <tr className="bg-gray-200">
                                                            {reportConfig.table.columns.map((column) => {
                                                                let cellValue = row[column.key];
                                                                
                                                                // Handle Actions column
                                                                if (column.key === 'Actions') {
                                                                    return (
                                                                        <td key={column.key} className="whitespace-nowrap px-4 py-3 text-slate-700">
                                                                            <button
                                                                                onClick={() => handleReprint(row)}
                                                                                className="inline-flex items-center gap-1 px-3 py-1.5 text-xs font-medium text-indigo-600 bg-indigo-50 rounded-lg hover:bg-indigo-100 transition-colors"
                                                                                title="Reprint Return Bill"
                                                                            >
                                                                                <FiPrinter size={14} />
                                                                                Reprint
                                                                            </button>
                                                                        </td>
                                                                    );
                                                                }

                                                                if (column.key === 'BillType') {
                                                                    return (
                                                                        <td key={column.key} className="px-4 py-3 align-top">
                                                                            <div className="flex flex-wrap items-center gap-1.5">
                                                                                {billMeta.hasIp ? (
                                                                                    <>
                                                                                        <span className="inline-flex items-center rounded-md bg-violet-100 px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wide text-violet-900 ring-1 ring-violet-400/80">
                                                                                            IP {billMeta.ipDisplay}
                                                                                        </span>
                                                                                        <span
                                                                                            className={`inline-flex items-center rounded-md px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wide ring-1 ${
                                                                                                billMeta.isCredit
                                                                                                    ? 'bg-amber-100 text-amber-950 ring-amber-400/90'
                                                                                                    : 'bg-emerald-100 text-emerald-950 ring-emerald-400/90'
                                                                                            }`}
                                                                                            title={billMeta.isCredit ? 'IP credit return' : 'IP debit return'}
                                                                                        >
                                                                                            {billMeta.creditDebitLabel}
                                                                                        </span>
                                                                                    </>
                                                                                ) : (
                                                                                    <span className="inline-flex items-center rounded-md bg-slate-100 px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wide text-slate-800 ring-1 ring-slate-400/80">
                                                                                        OP return
                                                                                    </span>
                                                                                )}
                                                                            </div>
                                                                        </td>
                                                                    );
                                                                }
                                                                
                                                                // Format specific columns
                                                                if (column.key === 'EntryDate') {
                                                                    cellValue = new Date(cellValue).toLocaleDateString('en-GB', {
                                                                        day: '2-digit',
                                                                        month: '2-digit',
                                                                        year: 'numeric'
                                                                    });
                                                                } else if (column.key === 'DiscountAmount') {
                                                                    cellValue = `₹${getRowDiscount(row).toFixed(2)}`;
                                                                } else if (column.key === 'NetAmount') {
                                                                    // Use NetAmount directly from API response
                                                                    cellValue = `₹${parseFloat(row.NetAmount || 0).toFixed(2)}`;
                                                                } else if (['TotalAmount', 'TaxAmount', 'BalanceDue'].includes(column.key)) {
                                                                    cellValue = `₹${parseFloat(cellValue || 0).toFixed(2)}`;
                                                                } else if (column.key === 'DoctorName' && (!cellValue || cellValue === null)) {
                                                                    cellValue = '-';
                                                                } else if (column.key === 'InvoiceNo') {
                                                                    cellValue = row.InvoiceNo;
                                                                }
                                                                
                                                                return (
                                                                    <td key={column.key} className="whitespace-nowrap px-4 py-3 text-slate-700">
                                                                        {cellValue}
                                                                    </td>
                                                                );
                                                            })}
                                                        </tr>
                                                        {items.length > 0 && (
                                                            <tr>
                                                                <td colSpan={reportConfig.table.columns.length} className="px-4 py-4 bg-slate-50">
                                                                    <div className="overflow-x-auto">
                                                                        <h4 className="text-sm font-semibold text-slate-800 mb-3 flex flex-wrap items-baseline gap-x-2 gap-y-1">
                                                                            <span>Invoice items</span>
                                                                            {billMeta.hasIp ? (
                                                                                <span className="text-xs font-normal text-slate-600">
                                                                                    <span className="text-violet-700 font-medium">IP {billMeta.ipDisplay}</span>
                                                                                    <span className="mx-1.5 text-slate-400">·</span>
                                                                                    <span className={billMeta.isCredit ? 'text-amber-800 font-medium' : 'text-emerald-800 font-medium'}>
                                                                                        {billMeta.creditDebitLabel}
                                                                                    </span>
                                                                                </span>
                                                                            ) : (
                                                                                <span className="text-xs font-normal text-slate-600">
                                                                                    <span className="font-medium text-slate-700">OP return</span>
                                                                                </span>
                                                                            )}
                                                                        </h4>
                                                                        <table className="min-w-full divide-y divide-slate-200 text-xs">
                                                                            <thead className="bg-slate-100">
                                                                                <tr>
                                                                                    <th className="px-3 py-2 text-left font-semibold text-slate-700">SI No</th>
                                                                                    <th className="px-3 py-2 text-left font-semibold text-slate-700">Item Name</th>
                                                                                    <th className="px-3 py-2 text-right font-semibold text-slate-700">Qty</th>
                                                                                    <th className="px-3 py-2 text-right font-semibold text-slate-700">MRP</th>
                                                                                    <th className="px-3 py-2 text-right font-semibold text-slate-700">Sales Price</th>
                                                                                    <th className="px-3 py-2 text-right font-semibold text-slate-700">Tax Amount</th>
                                                                                    <th className="px-3 py-2 text-right font-semibold text-slate-700">Net Total</th>
                                                                                </tr>
                                                                            </thead>
                                                                            <tbody className="bg-white divide-y divide-slate-200">
                                                                                {items.map((item, itemIndex) => (
                                                                                    <tr key={itemIndex} className="hover:bg-indigo-50/30">
                                                                                        <td className="px-3 py-2 text-slate-700">{itemIndex + 1}</td>
                                                                                        <td className="px-3 py-2 text-slate-700 font-medium">{item.itemName || '-'}</td>
                                                                                        <td className="px-3 py-2 text-slate-700 text-right">{parseFloat(item.qty || 0).toFixed(2)}</td>
                                                                                        <td className="px-3 py-2 text-slate-700 text-right">₹{parseFloat(item.mrp || 0).toFixed(2)}</td>
                                                                                        <td className="px-3 py-2 text-slate-700 text-right">₹{parseFloat(item.salesPrice || 0).toFixed(2)}</td>
                                                                                        <td className="px-3 py-2 text-slate-700 text-right">₹{parseFloat(item.taxAmount || 0).toFixed(2)}</td>
                                                                                        <td className="px-3 py-2 text-slate-700 text-right font-semibold">₹{parseFloat(item.netTotal || 0).toFixed(2)}</td>
                                                                                    </tr>
                                                                                ))}
                                                                            </tbody>
                                                                        </table>
                                                                    </div>
                                                                </td>
                                                            </tr>
                                                        )}
                                                    </React.Fragment>
                                                );
                                            })
                                        ) : (
                                            <tr>
                                                <td colSpan={reportConfig.table.columns.length} className="px-4 py-8 text-center text-slate-500">
                                                    No data available for the selected period
                                                </td>
                                            </tr>
                                        )}
                                    </tbody>
                                </table>
                            </div>
                        </section>
                    </div>
                </main>
            </div>

            {/* Return Print Component - Hidden */}
            {printData && (
                <div style={{ position: 'absolute', opacity: 0, top: '-10000px', left: '-10000px' }}>
                    <ReturnPrint
                        ref={returnPrintRef}
                        invoiceNumber={printData.invoiceNumber}
                        selectedPatient={printData.selectedPatient}
                        selectedDoctor={printData.selectedDoctor}
                        date={printData.date}
                        products={printData.products}
                        balance_due={printData.balance_due}
                        calculateTotal={printData.calculateTotal}
                        calculateGrandTotal={printData.calculateGrandTotal}
                        calculateTotalDiscount={printData.calculateTotalDiscount}
                        calculateTotalGST={printData.calculateTotalGST}
                        grand_total={printData.grand_total}
                        payingAmount={printData.payingAmount}
                        generalSetting={generalsettings}
                        sysInfo={sysInfo}
                        username={userInfo?.UserName}
                        setTotalPages={setTotalPages}
                        currentPage={currentPage}
                    />
                </div>
            )}

            {/* Export Format Modal */}
            <ExportFormatModal
                isOpen={showExportModal}
                onClose={() => setShowExportModal(false)}
                onSelectFormat={handleExportFormatSelect}
            />
        </div>
    );
};

export default withAuth(SalesReturnBillWise);